/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updatePregnancy } from "../../../states/slcObjectives.js";

export default function Pregnancy() {
  const pregnancy = useSelector(
    (state) => state.objectives.protection.pregnancy
  );

  const [prgFetusCount, setPrgFetusCount] = useState(pregnancy.prgFetusCount);
  const [prgCoverateAmt, setPrgCoverateAmt] = useState(
    pregnancy.prgCoverateAmt
  );
  const [prgTotalFundsRequired, setPrgTotalFundsRequired] = useState(
    pregnancy.prgTotalFundsRequired
  );
  const [prgExistingResource, setPrgExistingResource] = useState(
    pregnancy.prgExistingResource
  );
  const [prgSurplusShortfall, setPrgSurplusShortfall] = useState(
    pregnancy.prgSurplusShortfall
  );

  const [deviationReason, setDeviationReason] = useState(
    pregnancy.deviationReason
  );
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcTotalFundsRequired, [prgFetusCount, prgCoverateAmt]);

  useEffect(calcSurplusShortfall, [prgExistingResource, prgTotalFundsRequired]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcTotalFundsRequired() {
    let totalFundsRequired =
      Number(removeCommaFromCurrency(prgFetusCount)) *
      Number(removeCommaFromCurrency(prgCoverateAmt));

    totalFundsRequired = convertLocaleString(totalFundsRequired, "en-US", 2);

    updateRedux("prgTotalFundsRequired", totalFundsRequired, updatePregnancy);
    setPrgTotalFundsRequired(totalFundsRequired);
  }

  function calcSurplusShortfall() {
    let totalSurplusShortfall =
      Number(removeCommaFromCurrency(prgExistingResource)) -
      Number(removeCommaFromCurrency(prgTotalFundsRequired));

    totalSurplusShortfall = convertLocaleString(
      totalSurplusShortfall,
      "en-US",
      2
    );

    updateRedux("prgSurplusShortfall", totalSurplusShortfall, updatePregnancy);
    setPrgSurplusShortfall(totalSurplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    if (id === "prgFetusCount") {
      input_data.fieldName = "No Of Fetus";
      input_data.fieldVariable = prgFetusCount;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPrgFetusCount(result.value);
        updateRedux("prgFetusCount", result.value, updatePregnancy);
      }
    }

    if (id === "prgCoverateAmt") {
      input_data.fieldName = "Coverage Amount";
      input_data.fieldVariable = prgCoverateAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPrgCoverateAmt(result.value);
        updateRedux("prgCoverateAmt", result.value, updatePregnancy);
      }
    }

    if (id === "prgExistingResource") {
      input_data.fieldName = "Coverage Amount";
      input_data.fieldVariable = prgExistingResource;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPrgExistingResource(result.value);
        updateRedux("prgExistingResource", result.value, updatePregnancy);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updatePregnancy);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={1} size={12}>
        <GridSectionHeader
          sectionName="In the Event of Pregnancy–Complications/ Pregnancy–Related Death"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
        NO OF FETUS
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (A) No. of Fetus
          </Typography>
        </Grid>

        <GridTextfield
          id="prgFetusCount"
          key="prgFetusCount"
          columnSize="3"
          currState={prgFetusCount}
          onChange={handleChange}
        />

        {/*==============================
        COVERAGE AMOUNT
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (B) Coverage Amount
          </Typography>
        </Grid>

        <GridTextfield
          id="prgCoverateAmt"
          key="prgCoverateAmt"
          columnSize="3"
          isCurrency={true}
          currState={prgCoverateAmt}
          onChange={handleChange}
        />

        {/*==============================
        TOTAL FUNDS REQUIRED
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (A*B) = (C) Total Funds Required
          </Typography>
        </Grid>

        <GridTextfield
          id="prgTotalFundsRequired"
          key="prgTotalFundsRequired"
          columnSize="3"
          isCurrency={true}
          currState={prgTotalFundsRequired}
          onChange={handleChange}
          disabled={true}
        />

        {/*==============================
        EXISTING RESOURCES
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (D) Existing Resources (if any)
          </Typography>
        </Grid>

        <GridTextfield
          id="prgExistingResource"
          key="prgExistingResource"
          columnSize="3"
          isCurrency={true}
          currState={prgExistingResource}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        EXISTING HOSPITAL PLAN COVER
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (D-C) Surplus / Shotfall
          </Typography>
        </Grid>

        <GridTextfield
          id="prgSurplusShortfall"
          key="prgSurplusShortfall"
          columnSize="3"
          isCurrency={true}
          currState={prgSurplusShortfall}
          onChange={handleChange}
          disabled={true}
        />

        {/*==============================
        REASON(S) FOR DEVIATIONS
        ==============================*/}
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Reason(s) for Deviations:
        </Typography>
        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && (
          <FormHelperText error={deviationReasonError}>
            {deviationReasonError}
          </FormHelperText>
        )}
      </Grid>
    </>
  );
}
